import {
  type ProductType,
  PRODUCT_TYPE,
  type UlType,
} from "#src/constants/products.ts";
import { type ConceptType, CONCEPT_TYPE } from "#src/constants/concepts.ts";
import {
  CREDITING_RATES,
  LOAN_TYPES,
  MAX_INCOME_AGE,
  RIDER_PERCENTAGE_OPTIONS,
  VITALITY_LEVELS,
  PARTNER_DISCOUNT,
  PARTNER_DISCOUNT_REQUIREMENTS,
  FILTER_CHRONIC_ILLNESS_AND_LTC_OPTIONS,
  FILTER_CHRONIC_ILLNESS_AND_LTC,
  INCOME_SOLVES,
  INCOME_SOLVE,
  LOAN_TYPE,
  VITALITY_LEVEL,
  CREDITING_RATE,
  SOLVE,
  insuredQuoteData,
  type IncomeSolve,
  type QuoteRequestParams,
  type SectionedSolves,
  type Solve,
  type FilterChronicIllnessAndLtc,
  type PartnerDiscountDataItem,
  type SolveDataItem,
  type LoanTypeDataItem,
  type CreditingRateDataItem,
  type QuoteParams,
  type PayDurationDataItem,
  type AgeValueDataItem,
  type LapseProtectionToAgeDataItem,
  type RenderRules,
  type ModeDataItem,
  type QuoteScenarioData,
  type CreditingRate,
  type LoanType,
  type PartnerDiscount,
  type VitalityLevel,
  type ParsingData,
  parseType,
  parseBoolean,
  parseNumber,
} from "#src/types/QuoteParams.ts";
import { dateToYears } from "#src/util/helpers.ts";
import { BENEFIT_PERIOD_OPTIONS } from "#src/types/products/LinkedBenefit.ts";
import { type Mode, MODES } from "#src/constants/mode.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";

const DB_MIN_NON_MEC_ITEM: SolveDataItem = {
  value: SOLVE.MIN_NON_MEC_DEATH_BENEFIT,
  text: "Min Non-MEC Death Benefit",
  display: "initial_death_benefit",
};

const DB_TARGET_ITEM: SolveDataItem = {
  value: SOLVE.TARGET_DEATH_BENEFIT,
  text: "Target Death Benefit",
  display: "initial_death_benefit",
};

const DB_CASH_AT_AGE_ITEM: SolveDataItem = {
  value: SOLVE.DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE,
  text: "Death Benefit Solve Cash Value at Age",
  display: "initial_death_benefit",
};

const DB_LAPSE_PROTECTION_ITEM: SolveDataItem = {
  value: SOLVE.LAPSE_PROTECTION_DEATH_BENEFIT,
  text: "Lapse Protection Death Benefit",
  display: "initial_death_benefit",
};

const NO_SOLVE_ITEM: SolveDataItem = {
  value: SOLVE.SPECIFY_PREMIUM_AND_DEATH_BENEFIT,
  text: "No Solve",
};

const PREMIUM_MAX_NON_MEC_ITEM: SolveDataItem = {
  value: SOLVE.MAX_NON_MEC_PREMIUM,
  text: "Max Non-MEC Premium",
};

const PREMIUM_TARGET_ITEM: SolveDataItem = {
  value: SOLVE.TARGET_PREMIUM,
  text: "Target Premium",
};

const PREMIUM_CASH_AT_AGE_ITEM: SolveDataItem = {
  value: SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  text: "Premium Solve Cash Value at Age",
};

const PREMIUM_LAPSE_PROTECTION_ITEM: SolveDataItem = {
  value: SOLVE.LAPSE_PROTECTION_PREMIUM,
  text: SOLVE.LAPSE_PROTECTION_PREMIUM,
};

const requestCategories = {
  [PRODUCT_TYPE.ACCUMULATION_IUL]: "universal_life.indexed.accumulation",
  [PRODUCT_TYPE.PROTECTION_IUL]: "universal_life.indexed.protection",
  [PRODUCT_TYPE.PROTECTION_VUL]: "universal_life.protection.variable",
  [PRODUCT_TYPE.UNIVERSAL_LIFE]: "universal_life.none",
};

const displays = {
  [PRODUCT_TYPE.PROTECTION_IUL]: "Protection IUL",
  [PRODUCT_TYPE.PROTECTION_VUL]: "Protection VUL",
  [PRODUCT_TYPE.ACCUMULATION_IUL]: "Accumulation IUL",
  [PRODUCT_TYPE.UNIVERSAL_LIFE]: "Universal Life",
  [CONCEPT_TYPE.ALL_UL]: "All Universal Life",
};

const solves: Record<UlType | typeof CONCEPT_TYPE.ALL_UL, SectionedSolves> = {};

solves[PRODUCT_TYPE.ACCUMULATION_IUL] = {
  death_benefit_solves: [
    DB_MIN_NON_MEC_ITEM,
    DB_TARGET_ITEM,
    DB_CASH_AT_AGE_ITEM,
  ],
  no_solves: [NO_SOLVE_ITEM],
  premium_solves: [
    PREMIUM_MAX_NON_MEC_ITEM,
    PREMIUM_TARGET_ITEM,
    PREMIUM_CASH_AT_AGE_ITEM,
  ],
};

solves[PRODUCT_TYPE.PROTECTION_IUL] = {
  death_benefit_solves: [
    DB_MIN_NON_MEC_ITEM,
    DB_TARGET_ITEM,
    DB_CASH_AT_AGE_ITEM,
    DB_LAPSE_PROTECTION_ITEM,
  ],
  no_solves: [NO_SOLVE_ITEM],
  premium_solves: [
    PREMIUM_MAX_NON_MEC_ITEM,
    PREMIUM_TARGET_ITEM,
    PREMIUM_CASH_AT_AGE_ITEM,
    PREMIUM_LAPSE_PROTECTION_ITEM,
  ],
};

solves[PRODUCT_TYPE.PROTECTION_VUL] = {
  death_benefit_solves: [
    DB_MIN_NON_MEC_ITEM,
    DB_TARGET_ITEM,
    DB_CASH_AT_AGE_ITEM,
    DB_LAPSE_PROTECTION_ITEM,
  ],
  no_solves: [NO_SOLVE_ITEM],
  premium_solves: [
    PREMIUM_MAX_NON_MEC_ITEM,
    PREMIUM_TARGET_ITEM,
    PREMIUM_CASH_AT_AGE_ITEM,
    PREMIUM_LAPSE_PROTECTION_ITEM,
  ],
};

solves[PRODUCT_TYPE.UNIVERSAL_LIFE] = {
  death_benefit_solves: [
    DB_MIN_NON_MEC_ITEM,
    DB_CASH_AT_AGE_ITEM,
    DB_LAPSE_PROTECTION_ITEM,
  ],
  no_solves: [NO_SOLVE_ITEM],
  premium_solves: [
    PREMIUM_MAX_NON_MEC_ITEM,
    PREMIUM_TARGET_ITEM,
    PREMIUM_CASH_AT_AGE_ITEM,
    PREMIUM_LAPSE_PROTECTION_ITEM,
  ],
};

solves[CONCEPT_TYPE.ALL_UL] = {
  death_benefit_solves: [
    DB_MIN_NON_MEC_ITEM,
    DB_TARGET_ITEM,
    DB_CASH_AT_AGE_ITEM,
    DB_LAPSE_PROTECTION_ITEM,
  ],
  no_solves: [NO_SOLVE_ITEM],
  premium_solves: [
    PREMIUM_MAX_NON_MEC_ITEM,
    PREMIUM_TARGET_ITEM,
    PREMIUM_CASH_AT_AGE_ITEM,
    PREMIUM_LAPSE_PROTECTION_ITEM,
  ],
};

const defaults: Record<
  UlType | typeof CONCEPT_TYPE.ALL_UL,
  UniversalLifeProductType["defaults"]
> = {};

defaults[PRODUCT_TYPE.ACCUMULATION_IUL] = {
  solve: SOLVE.MAX_NON_MEC_PREMIUM,
  pay_duration: -1,
  partner_discount: PARTNER_DISCOUNT.SINGLE,
  cash_value: 1,
  age_value: 120,
  lapse_protection_to_age: 100,
  crediting_rate: CREDITING_RATE.DEFAULT_RATE,
  filter_chronic_illness_and_ltc:
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC,
  ltc_rider_percentage: 4,
  income_solve: INCOME_SOLVE.MAX,
  income_start_age: 66,
  income_end_age: 90,
  loan_type: LOAN_TYPE.FIXED,
  vitality_level_filter: VITALITY_LEVEL.BRONZE,
  subtype: null,
  save_age: false,
};

defaults[PRODUCT_TYPE.PROTECTION_IUL] = {
  solve: SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  pay_duration: -1,
  partner_discount: PARTNER_DISCOUNT.SINGLE,
  cash_value: 1,
  age_value: 120,
  lapse_protection_to_age: 100,
  crediting_rate: CREDITING_RATE.DEFAULT_RATE,
  filter_chronic_illness_and_ltc:
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC,
  ltc_rider_percentage: 4,
  income_solve: INCOME_SOLVE.NO,
  income_start_age: 66,
  income_end_age: 90,
  loan_type: LOAN_TYPE.FIXED,
  vitality_level_filter: VITALITY_LEVEL.BRONZE,
  subtype: null,
  save_age: false,
};

defaults[PRODUCT_TYPE.PROTECTION_VUL] = {
  solve: SOLVE.LAPSE_PROTECTION_PREMIUM,
  pay_duration: -1,
  partner_discount: PARTNER_DISCOUNT.SINGLE,
  cash_value: 1,
  age_value: 120,
  lapse_protection_to_age: 100,
  crediting_rate: CREDITING_RATE.DEFAULT_RATE,
  filter_chronic_illness_and_ltc:
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC,
  ltc_rider_percentage: 4,
  income_solve: INCOME_SOLVE.NO,
  income_start_age: 66,
  income_end_age: 90,
  loan_type: LOAN_TYPE.FIXED,
  vitality_level_filter: VITALITY_LEVEL.BRONZE,
  subtype: null,
  save_age: false,
};

defaults[PRODUCT_TYPE.UNIVERSAL_LIFE] = {
  solve: SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  pay_duration: -1,
  partner_discount: PARTNER_DISCOUNT.SINGLE,
  cash_value: 1,
  age_value: 120,
  lapse_protection_to_age: 120,
  crediting_rate: CREDITING_RATE.DEFAULT_RATE,
  filter_chronic_illness_and_ltc:
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC,
  ltc_rider_percentage: 4,
  income_solve: INCOME_SOLVE.NO,
  income_start_age: 66,
  income_end_age: 90,
  loan_type: LOAN_TYPE.FIXED,
  vitality_level_filter: VITALITY_LEVEL.BRONZE,
  subtype: null,
  save_age: false,
};

defaults[CONCEPT_TYPE.ALL_UL] = {
  solve: SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  pay_duration: -1,
  partner_discount: PARTNER_DISCOUNT.SINGLE,
  cash_value: 1,
  age_value: 120,
  lapse_protection_to_age: 120,
  crediting_rate: CREDITING_RATE.DEFAULT_RATE,
  filter_chronic_illness_and_ltc:
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC,
  ltc_rider_percentage: 4,
  income_solve: INCOME_SOLVE.NO,
  income_start_age: 66,
  income_end_age: 90,
  loan_type: LOAN_TYPE.FIXED,
  vitality_level_filter: VITALITY_LEVEL.BRONZE,
  subtype: null,
  save_age: false,
};

function showPremium(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.MIN_NON_MEC_DEATH_BENEFIT,
    SOLVE.TARGET_DEATH_BENEFIT,
    SOLVE.DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE,
    SOLVE.LAPSE_PROTECTION_DEATH_BENEFIT,
    SOLVE.SPECIFY_PREMIUM_AND_DEATH_BENEFIT,
  ];

  return items.includes(params.solve);
}

function showFaceAmount(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.SPECIFY_PREMIUM_AND_DEATH_BENEFIT,
    SOLVE.MAX_NON_MEC_PREMIUM,
    SOLVE.TARGET_PREMIUM,
    SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
    SOLVE.LAPSE_PROTECTION_PREMIUM,
  ];
  return items.includes(params.solve);
}

function showLapseProtectionToAge(params: QuoteParams) {
  if (!params.solve) return false;
  const items: Solve[] = [
    SOLVE.LAPSE_PROTECTION_DEATH_BENEFIT,
    SOLVE.LAPSE_PROTECTION_PREMIUM,
  ];

  return items.includes(params.solve);
}

function showIncomeSolve(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.MIN_NON_MEC_DEATH_BENEFIT,
    SOLVE.TARGET_DEATH_BENEFIT,
    SOLVE.SPECIFY_PREMIUM_AND_DEATH_BENEFIT,
    SOLVE.MAX_NON_MEC_PREMIUM,
    SOLVE.TARGET_PREMIUM,
  ];
  return items.includes(params.solve);
}

function showCashValue(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE,
    SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  ];

  return items.includes(params.solve);
}

function showAgeValue(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE,
    SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  ];
  return items.includes(params.solve);
}

function showCreditingRate(params: QuoteParams) {
  if (!params.solve) return false;

  const items: Solve[] = [
    SOLVE.MIN_NON_MEC_DEATH_BENEFIT,
    SOLVE.TARGET_DEATH_BENEFIT,
    SOLVE.DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE,
    SOLVE.SPECIFY_PREMIUM_AND_DEATH_BENEFIT,
    SOLVE.MAX_NON_MEC_PREMIUM,
    SOLVE.TARGET_PREMIUM,
    SOLVE.PREMIUM_SOLVE_CASH_VALUE_AT_AGE,
  ];
  return items.includes(params.solve);
}
function showLoanType(params: QuoteParams) {
  if (!showIncomeSolve(params)) return false;

  return params.income_solve === INCOME_SOLVE.MAX;
}

function showLtcRiderPercentage(params: QuoteParams) {
  if (!params.filter_chronic_illness_and_ltc) return false;

  const items: FilterChronicIllnessAndLtc[] = [
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_CHRONIC_ILLNESS_LTC_RIDER,
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_FULL_CHRONIC_ILLNESS_LTC_RIDER,
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_LTC_RIDER,
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_CHRONIC_ILLNESS_RIDER,
  ];
  return items.includes(params.filter_chronic_illness_and_ltc);
}

function showPartnerDiscount(params: QuoteParams) {
  if (!params.filter_chronic_illness_and_ltc) return false;

  const items: FilterChronicIllnessAndLtc[] = [
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_CHRONIC_ILLNESS_LTC_RIDER,
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_FULL_CHRONIC_ILLNESS_LTC_RIDER,
    FILTER_CHRONIC_ILLNESS_AND_LTC.WITH_LTC_RIDER,
  ];

  return items.includes(params.filter_chronic_illness_and_ltc);
}

function showIncomeAges(params: QuoteParams) {
  if (!showIncomeSolve(params)) return false;
  return params.income_solve === INCOME_SOLVE.MAX;
}

function showHealth(conversion: boolean) {
  return !conversion;
}

export interface UniversalLifeProductType extends ProductBase {
  subtype: null | UlType;
  fetchableTypes: string[];
  solves: SectionedSolves;
  allSolves: SolveDataItem[];
  isPermanent: boolean;
  partnerDiscountOptions: PartnerDiscountDataItem[];
  incomeSolves: IncomeSolve[];
  ltcRiderOptions: FilterChronicIllnessAndLtc[];
  ltcRiderPercentageOptions: number[];
  loanTypes: LoanTypeDataItem[];
  creditingRates: CreditingRateDataItem[];
  permitFetchableType: (type: UlType) => void;
  advancedRenderRules: (
    params: QuoteParams,
    extraData?: { conversion: boolean }
  ) => Record<string, any>;
  toQuoteScenario: (args: QuoteScenarioData) => Partial<QuoteRequestParams>;
  generatePayDurationOptions: (birthdate: string) => PayDurationDataItem[];
  generateAgeValueOptions: (birthdate: string) => AgeValueDataItem[];
  generateLapseProtectionToAgeOptions: (
    birthdate: string
  ) => LapseProtectionToAgeDataItem[];
  generateIncomeAges: (birthdate: string) => number[];

  defaults: {
    age_value: number;
    cash_value: number;
    crediting_rate: CreditingRate;
    filter_chronic_illness_and_ltc: FilterChronicIllnessAndLtc;
    income_end_age: number;
    income_solve: IncomeSolve;
    income_start_age: number;
    lapse_protection_to_age: number;
    loan_type: LoanType;
    ltc_rider_percentage: number;
    partner_discount: PartnerDiscount;
    pay_duration: number;
    save_age: boolean;
    solve: Solve;
    subtype: null;
    vitality_level_filter: VitalityLevel;
  };

  eAppParser(args: Partial<ParsingData>): {
    face_amount: number;
    mode: Mode;
    ltc_rider_percentage?: number;
    benefit_period?: number;
    premium?: number;
    section_1035_exchange_amount: number | null;
  } & Omit<
    UniversalLifeProductType["defaults"],
    "ltc_rider_percentage" | "subtype"
  >;
}

// Utilized by PIUL, PVUL, UL, AIUL
function BaseUniversalLife(
  type: UlType | typeof CONCEPT_TYPE.ALL_UL
): UniversalLifeProductType {
  let requestCategory = "";
  const categories: string[] = [];
  const fetchableTypes: string[] = [];

  if (type) {
    requestCategory = requestCategories[type];
    if (requestCategory) {
      categories.push(requestCategory);
      fetchableTypes.push(type);
    }
  }

  return {
    get type(): ConceptType {
      return CONCEPT_TYPE.ALL_UL;
    },
    get subtype(): ProductType | null {
      if (type === CONCEPT_TYPE.ALL_UL) return null;
      return type;
    },
    get fetchableTypes() {
      return fetchableTypes;
    },
    get solves(): SectionedSolves {
      return solves[type];
    },
    get display(): string {
      return displays[type];
    },
    get requestCategory(): string {
      return requestCategory;
    },
    get allSolves(): SolveDataItem[] {
      const all: SolveDataItem[] = [];
      for (const solveType of Object.values(solves[type])) {
        all.push(...solveType);
      }
      return all;
    },
    get isPermanent(): boolean {
      return true;
    },
    get partnerDiscountOptions(): PartnerDiscountDataItem[] {
      return [
        {
          text: "Single",
          value: PARTNER_DISCOUNT.SINGLE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
        {
          text: "Married, Applying Individually",
          value: PARTNER_DISCOUNT.SPOUSE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.BASIC,
        },
      ];
    },
    get defaults() {
      return { ...defaults[type], categories };
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    get incomeSolves(): IncomeSolve[] {
      return INCOME_SOLVES;
    },
    get ltcRiderOptions(): FilterChronicIllnessAndLtc[] {
      return FILTER_CHRONIC_ILLNESS_AND_LTC_OPTIONS;
    },
    get ltcRiderPercentageOptions(): number[] {
      return RIDER_PERCENTAGE_OPTIONS;
    },
    get loanTypes(): LoanTypeDataItem[] {
      return LOAN_TYPES;
    },
    get creditingRates(): CreditingRateDataItem[] {
      return CREDITING_RATES;
    },
    permitFetchableType(type): void {
      if (!requestCategories[type]) throw "Must be a supported type";
      fetchableTypes.push(type);
      categories.push(requestCategories[type]);
    },
    renderRules(params, options = { conversion: false }): Partial<RenderRules> {
      const incomeAges = showIncomeAges(params);
      return {
        ageValue: showAgeValue(params),
        cashValue: showCashValue(params),
        faceAmount: showFaceAmount(params),
        incomeStartAge: incomeAges,
        incomeEndAge: incomeAges,
        incomeSolve: showIncomeSolve(params),
        lapseProtectionToAge: showLapseProtectionToAge(params),
        saveAge: true,
        subtype: true,
        ltcRider: true,
        ltcRiderPercentage: showLtcRiderPercentage(params),
        payDuration: true,
        partnerDiscount: showPartnerDiscount(params),
        premium: showPremium(params),
        insuredHealth: showHealth(options.conversion),
        solve: true,
        mode: true,
        state: true,
      };
    },
    advancedRenderRules(params: QuoteParams): Partial<RenderRules> {
      return {
        section1035ExchangeAmount: true,
        loanType: showLoanType(params),
        creditingRate: showCreditingRate(params),
      };
    },
    eAppParser(model): ReturnType<UniversalLifeProductType["eAppParser"]> {
      let partnerDiscount = model?.partner_discount;
      if (partnerDiscount === PARTNER_DISCOUNT.JOINT) {
        partnerDiscount = PARTNER_DISCOUNT.SPOUSE;
      }

      let income_start_age = model?.income_start_age;
      let income_end_age = model?.income_end_age;
      if (model?.age) {
        if (!income_start_age || income_start_age < model.age) {
          income_start_age = Math.max(model.age + 10, 66);
          income_end_age = Math.max(income_start_age + 10, 90);
        }
      }

      if (!income_start_age) income_start_age = this.defaults.income_start_age;
      if (!income_end_age) income_end_age = this.defaults.income_end_age;

      let section_1035_exchange_amount = null;
      if (model?.section_1035_exchange_amount) {
        section_1035_exchange_amount = +model.section_1035_exchange_amount;
      }

      const parsedData: ReturnType<UniversalLifeProductType["eAppParser"]> = {
        mode: model.mode as Mode,
        benefit_period: parseNumber(
          model.benefit_period,
          undefined,
          BENEFIT_PERIOD_OPTIONS.map(v => v.value)
        ),
        premium: parseNumber(model.premium, undefined),
        solve: parseType<Solve>(
          model.solve,
          this.allSolves.map(s => s.value),
          this.defaults.solve
        ),
        pay_duration: parseNumber(
          model.pay_duration,
          this.defaults.pay_duration
        ),
        cash_value: model?.cash_value || this.defaults.cash_value,
        age_value: parseNumber(model.age_value, this.defaults.age_value),
        lapse_protection_to_age: parseNumber(
          model.lapse_protection_to_age,
          this.defaults.lapse_protection_to_age
        ),
        crediting_rate: parseType<CreditingRate>(
          model.crediting_rate,
          CREDITING_RATES.map(v => v.value),
          this.defaults.crediting_rate
        ),
        filter_chronic_illness_and_ltc: parseType<FilterChronicIllnessAndLtc>(
          model.filter_chronic_illness_and_ltc,
          this.ltcRiderOptions,
          this.defaults.filter_chronic_illness_and_ltc
        ),
        ltc_rider_percentage: parseType<number>(
          model.ltc_rider_percentage,
          this.ltcRiderPercentageOptions,
          this.defaults.ltc_rider_percentage
        ),
        income_solve: parseType<IncomeSolve>(
          model.income_solve,
          this.incomeSolves,
          this.defaults.income_solve
        ),
        income_start_age,
        income_end_age,
        loan_type: parseType<LoanType>(
          model.loan_type,
          LOAN_TYPES.map(v => v.value),
          this.defaults.loan_type
        ),
        section_1035_exchange_amount,
        face_amount: parseNumber(
          model?.face_amount ||
            ("initial_death_benefit" in model
              ? model.initial_death_benefit
              : 0),
          1000000
        ),
        vitality_level_filter: parseType<VitalityLevel>(
          model.vitality_level_filter,
          VITALITY_LEVELS.map(v => v.value),
          this.defaults.vitality_level_filter
        ),
        partner_discount: parseType<PartnerDiscount>(
          partnerDiscount,
          this.partnerDiscountOptions.map(v => v.value),
          this.defaults.partner_discount
        ),
        save_age: parseBoolean(model.save_age, this.defaults.save_age),
      };

      if (
        parsedData.filter_chronic_illness_and_ltc ===
        FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC
      ) {
        parsedData.ltc_rider_percentage = undefined;
      }

      return parsedData;
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      return {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.subtype || args.params.selected_type,
        state: args.params.state,

        mode: args.params.mode,
        ...this.toQuoteScenario(args),
      };
    },
    toQuoteScenario(args): Partial<QuoteRequestParams> {
      let income_start_age = args.params.income_start_age,
        income_end_age = args.params.income_end_age;

      if (args.params.income_solve === INCOME_SOLVE.MAX) {
        income_start_age = income_start_age || this.defaults.income_start_age;
        income_end_age = income_end_age || this.defaults.income_end_age;
      }

      const body: Partial<QuoteRequestParams> = {
        age_value: args.params.age_value,
        cash_value: args.params.cash_value,
        crediting_rate: args.params.crediting_rate,
        filter_chronic_illness_and_ltc:
          args.params.filter_chronic_illness_and_ltc,
        income_solve: args.params.income_solve,
        income_start_age,
        income_end_age,
        lapse_protection_to_age: args.params.lapse_protection_to_age,
        loan_type: args.params.loan_type,
        pay_duration: args.params.pay_duration,
        partner_discount: args.params.partner_discount,
        solve: args.params.solve,
        vitality_level_filter: args.params.vitality_level_filter,
        save_age: args.params.save_age,
      };

      if (
        args.params.filter_chronic_illness_and_ltc !==
        FILTER_CHRONIC_ILLNESS_AND_LTC.WITHOUT_CHRONIC_LTC
      ) {
        body.ltc_rider_percentage = args.params.ltc_rider_percentage;
      }

      if (args.params.section_1035_exchange_amount) {
        body.section_1035_exchange = {
          enabled: true,
          amount: args.params.section_1035_exchange_amount,
        };
      }

      if (args.params.subtype) {
        body.categories = [requestCategories[args.params.subtype]];
      } else {
        body.categories = categories;
      }

      const renderRules = this.renderRules(args.params, {
        conversion: Boolean(args.conversion),
      });

      if (renderRules.premium) {
        body.premium = args.params.premium;
      }
      if (renderRules.faceAmount) {
        body.face_amount = args.params.face_amount;
      }

      return body;
    },
    generatePayDurationOptions(birthdate): PayDurationDataItem[] {
      const years = dateToYears(birthdate);
      const payOptions = [];
      for (let i = years + 1; i <= 100; i++) {
        payOptions.push({
          value: i - years,
          title: `${i - years} Year${i - years === 1 ? "" : "s"} / To age ${i}`,
        });
      }
      payOptions.push({
        value: -1,
        title: "Life",
      });
      return payOptions;
    },
    generateAgeValueOptions(birthdate): AgeValueDataItem[] {
      const years = dateToYears(birthdate);

      const ages = [];
      for (let i = years + 1; i <= 120; i++) {
        ages.push({ text: `Age ${i}`, value: i });
      }
      return ages;
    },
    generateLapseProtectionToAgeOptions(
      birthdate
    ): LapseProtectionToAgeDataItem[] {
      const years = dateToYears(birthdate);
      const ages = [];
      for (let i = years + 1; i <= 120; i++) {
        ages.push({ text: `Age ${i}`, value: i });
      }
      return ages;
    },
    generateIncomeAges(birthdate): number[] {
      const years = dateToYears(birthdate);
      const ages = [];
      for (let i = 0; i < MAX_INCOME_AGE - years; i++) {
        ages.push(years + i);
      }
      return ages;
    },
  };
}

export function AccumulationIUL() {
  return BaseUniversalLife(PRODUCT_TYPE.ACCUMULATION_IUL);
}

export function ProtectionIUL() {
  return BaseUniversalLife(PRODUCT_TYPE.PROTECTION_IUL);
}

export function ProtectionVUL() {
  return BaseUniversalLife(PRODUCT_TYPE.PROTECTION_VUL);
}

export function UniversalLife() {
  return BaseUniversalLife(PRODUCT_TYPE.UNIVERSAL_LIFE);
}

export function AllUniversalLife() {
  return BaseUniversalLife(CONCEPT_TYPE.ALL_UL);
}
